/**
 * Entity-relationship diagrams for course-provided SQL schemas.
 *
 * A schema listing tells a student which columns exist; it says nothing about
 * how the tables relate, which is the part a join question actually depends on.
 * Courses hand us a diagram for that, and this maps a question's schema to the
 * right one.
 *
 * Matching is on the exact set of table names rather than the course, because a
 * single course can use more than one schema -- CS 4400 practice sets use the
 * six-table company database while its exam material uses a much larger one. An
 * exact match means a question whose schema we have no diagram for simply shows
 * no diagram, instead of showing a misleading one.
 */

const DIAGRAMS = [
  {
    id: "company",
    src: "/images/schema-diagrams/company.jpg",
    title: "Company database",
    alt:
      "Entity-relationship diagram of the company database, showing employee, " +
      "department, project, dependent, dept_locations and works_on and the " +
      "relationships between them.",
    tables: [
      "employee",
      "dependent",
      "department",
      "dept_locations",
      "project",
      "works_on",
    ],
  },
];

const normalize = (name) => String(name || "").trim().toLowerCase();

/**
 * Find the diagram for a parsed schema, or null when we don't have one.
 * @param {{tables?: Array<{name?: string}>}} schema
 */
export function findSchemaDiagram(schema) {
  const tables = schema?.tables;
  if (!Array.isArray(tables) || tables.length === 0) return null;

  const present = new Set(tables.map((table) => normalize(table?.name)).filter(Boolean));
  if (present.size === 0) return null;

  return (
    DIAGRAMS.find((diagram) => {
      if (diagram.tables.length !== present.size) return false;
      return diagram.tables.every((name) => present.has(normalize(name)));
    }) || null
  );
}

export default findSchemaDiagram;
